import { colors } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ReactComponent as CheckboxCheckedIcon } from "./icons/checkbox_checked.svg";
import { ReactComponent as CheckboxIndeterminateIcon } from "./icons/checkbox_indeterminate.svg";
import typography from "./typography";
import shadows from "./shadows";
import fontFace from "./fontFace";

const baseTheme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: fontFace
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1180,
      xl: 1400
    }
  },
  palette: {
    background: {
      default: colors.common.white,
      paper: colors.common.white
    },
    brandColor: "#007dba",
    primary: {
      smoke: "#f8fcfd",
      grey2: "#ECF6FA",
      smokeBorder: "#DAE4E9",
      light: "#56798D",
      canard: "#005379",
      main: "#003b60",
      dark: "#002238",
      contrastText: colors.common.white
    },
    secondary: {
      main: "#31FEFD",
      smoke: "#E5FDFF",
      contrastText: "#002238",
    },
    text: {
      dark: "#052643",
      primary: "#003b60",
      secondary: "#4C7A8F",
      light: "#c6d2d8",
      grey: "#b4b4b4",
      disabled: "#B6C4C9",
      white: colors.common.white,
    },
    info: { main: "#F8F8F8" }
  },
  typography,
  shape: {
    borderRadius: 7
  },
  shadows: shadows()
});

const theme = createTheme(baseTheme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: fontFace
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: "900"
        }
      }
    },
    MuiSelect: {
      defaultProps: {
        size: "small",
        IconComponent: KeyboardArrowDownIcon
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: "600",
          padding: "0.625rem 1rem"
        }
      },
      defaultProps: {
        disableElevation: true
      }
    },
    MuiBadge: {
      styleOverrides: {
        dot: {
          width: 10,
          height: 10,
          margin: "3px",
          borderRadius: "50%"
        }
      }
    },
    MuiCheckbox: {
      defaultProps: {
        color: "secondary",
        checkedIcon: <CheckboxCheckedIcon />,
        indeterminateIcon: <CheckboxIndeterminateIcon />
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          width: "100%",
          border: "none"
        },
        columnHeaders: {
          backgroundColor: baseTheme.palette.primary.grey2,
          borderRadius: 0
        },
        columnHeader: {
          "&:not(:first-of-type)": {
            borderLeft: "solid 1px #0000001A"
          }
        },
        row: {
          "&.Mui-selected": {
            backgroundColor: baseTheme.palette.primary.smoke
          }
        },
        footerContainer: {
          backgroundColor: baseTheme.palette.primary.canard,
          color: "white",
          ".MuiTablePagination-toolbar": {
            color: "white"
          },
          ".MuiSvgIcon-root": {
            color: "white"
          }
        },
      },
      defaultProps: {
        autoHeight: true,
        disableRowSelectionOnClick: true
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:nth-child(even)": {
            backgroundColor: baseTheme.palette.primary.smoke
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          "&:not(.MuiTableCell-head)": {
            borderBottom: 0
          },
          "&.MuiTableCell-head": {
            fontWeight: 900,
            paddingBottom: 1
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          overflow: "visible",
          fontStyle: "italic",
          transform: "translate(1.5em, 1em) scale(1)",
          "&.Mui-focused, &.MuiFormLabel-filled": {
            transform: "translate(14px, -9px) scale(0.75)",
            fontStyle: "normal"
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "&.MuiOutlinedInput-root": {
            borderColor: "#C3D2D9",
            borderRadius: 10
          },
          "&.Mui-disabled .MuiInputBase-input": {
            color: baseTheme.palette.text.disabled,
            "-webkit-text-fill-color": "currentColor"
          },
        },
        input: {
          "&.MuiInputBase-input": {
            padding: "1em 1.5em"
          },
          ".MuiInputBase-input::placeholder": {
            fontWeight: "300"
          }
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontSize: "12px",
          textTransform: "none",
          whiteSpace: "nowrap",
          "&.Mui-selected": {
            backgroundColor: baseTheme.palette.primary.main,
            color: baseTheme.palette.secondary.main,
            pointerEvents: "none"
          }
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          fontFamily: "inherit"
        }
      }
    }
  }
});

export default theme;
